// Thematic map: reads every value straight off the raw `.inp` row, in
// whatever unit the project's own author typed it in — no units.ts feet/cfs
// round-trip, since this is a pure display/coloring feature, not a physics
// calculation.

import type { ElementCategory } from "./properties.ts";
import type { UnitKind } from "./sectionSchema.ts";
import type { TextKey } from "../../i18n/en.ts";
// `model/results/resultsVariables.ts` imports back from `model/inp/properties.ts`
// — a real bidirectional coupling between `model/inp` and `model/results`, not a
// literal file cycle today (deep-review dependency-mapper finding, Medium). No
// current lint guard against it becoming one.
import type { SimulationResults } from "../results/simulationResults.ts";

function readColumn(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  index: number
): Map<string, number> {
  const result = new Map<string, number>();
  for (const row of raw.get(section) ?? []) {
    if (!row[0]) continue;
    const v = Number(row[index]);
    if (Number.isFinite(v)) result.set(row[0], v);
  }
  return result;
}

function mergeInto(target: Map<string, number>, source: Map<string, number>): Map<string, number> {
  for (const [k, v] of source) target.set(k, v);
  return target;
}

/**
 * Computed by a loop rather than `Math.max(...series)` — spreading a long
 * series as call arguments hits V8's function-argument limit (`RangeError:
 * Maximum call stack size exceeded`) on a long/fine-report-step DYNWAVE run.
 * Caller must pass a non-empty array.
 */
function maxOf(series: readonly number[]): number {
  let max = series[0];
  for (let i = 1; i < series.length; i++) {
    if (series[i] > max) max = series[i];
  }
  return max;
}

function readNodeInvertElevation(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return mergeInto(
    mergeInto(readColumn(raw, "JUNCTIONS", 1), readColumn(raw, "OUTFALLS", 1)),
    readColumn(raw, "STORAGE", 1)
  );
}

function readNodeMaxDepth(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return mergeInto(readColumn(raw, "JUNCTIONS", 2), readColumn(raw, "STORAGE", 2));
}

/** CIRCULAR conduits only — every other shape's Geom1 means something else entirely (a rectangle's height, an egg's own scale factor...), so mixing them into one "diameter" theme would compare unlike quantities. */
function readLinkDiameter(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  const result = new Map<string, number>();
  for (const row of raw.get("XSECTIONS") ?? []) {
    if (row[0] && row[1] === "CIRCULAR") {
      const v = Number(row[2]);
      if (Number.isFinite(v)) result.set(row[0], v);
    }
  }
  return result;
}

function readLinkLength(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return readColumn(raw, "CONDUITS", 3);
}

function readLinkRoughness(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return readColumn(raw, "CONDUITS", 4);
}

function readSubcatchmentArea(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return readColumn(raw, "SUBCATCHMENTS", 3);
}

function readSubcatchmentImperv(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return readColumn(raw, "SUBCATCHMENTS", 4);
}

function readSubcatchmentWidth(raw: ReadonlyMap<string, string[][]>): Map<string, number> {
  return readColumn(raw, "SUBCATCHMENTS", 5);
}

interface ThematicAttribute {
  key: string;
  category: Extract<ElementCategory, "node" | "link" | "subcatchment">;
  labelKey: TextKey;
  read: (
    raw: ReadonlyMap<string, string[][]>,
    results: SimulationResults | null
  ) => Map<string, number>;
  requiresResults?: boolean;
  /** `linkDiameter` is unambiguous here even though the property panel shows no unit for raw XSECTIONS Geom1 (it's polymorphic there) — `readLinkDiameter` already filters to CIRCULAR only, where Geom1 is always a real length. */
  unitKind: UnitKind;
}

function readResultNodeMaxDepth(
  _raw: ReadonlyMap<string, string[][]>,
  results: SimulationResults | null
): Map<string, number> {
  const out = new Map<string, number>();
  if (!results) return out;
  for (const [name, depths] of results.nodeDepth) {
    if (depths.length > 0) out.set(name, maxOf(depths));
  }
  return out;
}

function readResultLinkMaxFlow(
  _raw: ReadonlyMap<string, string[][]>,
  results: SimulationResults | null
): Map<string, number> {
  const out = new Map<string, number>();
  if (!results) return out;
  for (const [name, flows] of results.linkFlow) {
    if (flows.length > 0) out.set(name, maxOf(flows));
  }
  return out;
}

export const THEMATIC_ATTRIBUTES: ThematicAttribute[] = [
  {
    key: "nodeInvert",
    category: "node",
    labelKey: "thematicMap.nodeInvert",
    read: readNodeInvertElevation,
    unitKind: "length",
  },
  {
    key: "nodeMaxDepth",
    category: "node",
    labelKey: "thematicMap.nodeMaxDepth",
    read: readNodeMaxDepth,
    unitKind: "length",
  },
  {
    key: "linkDiameter",
    category: "link",
    labelKey: "thematicMap.linkDiameter",
    read: readLinkDiameter,
    unitKind: "length",
  },
  {
    key: "linkLength",
    category: "link",
    labelKey: "thematicMap.linkLength",
    read: readLinkLength,
    unitKind: "length",
  },
  {
    key: "linkRoughness",
    category: "link",
    labelKey: "thematicMap.linkRoughness",
    read: readLinkRoughness,
    unitKind: "dimensionless",
  },
  {
    key: "subArea",
    category: "subcatchment",
    labelKey: "thematicMap.subArea",
    read: readSubcatchmentArea,
    unitKind: "landarea",
  },
  {
    key: "subImperv",
    category: "subcatchment",
    labelKey: "thematicMap.subImperv",
    read: readSubcatchmentImperv,
    unitKind: "percent",
  },
  {
    key: "subWidth",
    category: "subcatchment",
    labelKey: "thematicMap.subWidth",
    read: readSubcatchmentWidth,
    unitKind: "length",
  },
  {
    key: "resultNodeMaxDepth",
    category: "node",
    labelKey: "thematicMap.resultNodeMaxDepth",
    read: readResultNodeMaxDepth,
    requiresResults: true,
    unitKind: "length",
  },
  {
    key: "resultLinkMaxFlow",
    category: "link",
    labelKey: "thematicMap.resultLinkMaxFlow",
    read: readResultLinkMaxFlow,
    requiresResults: true,
    unitKind: "flow",
  },
];
